<script setup>
//#region import
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { format } from 'date-fns';
import { cloneDeep } from 'lodash-es';
//#endregion

//#region props
const props = defineProps({
  currency: {
    type: String,
    default: () => '',
  },
  column: {
    type: Object,
    required: true,
  },
  fields: {
    type: Array,
    required: true,
  },
  customSorting: {
    type: Boolean,
    default: () => false,
  },
  sortProp: {
    type: String,
    required: true,
  },
  sortOrder: {
    type: String,
    required: true,
  },
  lastOpenFilter: {
    type: String,
    required: true,
  },
  columnFilters: {
    type: Object,
    required: true,
  },
  isFiltersInit: {
    type: Boolean,
    required: true,
  },
  customFilterColumn: {
    type: Array,
    required: true,
  },
  customFilterColumnCreated: {
    type: Array,
    required: true,
  },
});
//#endregion

//#region emit
const emit = defineEmits([
  'handleSort',
  'lastOpenFilter',
  'handlerColumnFilterInput',
  'handlerRangeValue',
  'handlerDatepickerRange',
  'setCheckApplyFilter',
  'filter',
  'changeColumnFilters',
  'handlerRadioBtn',
  'changeExclude',
]);
//#endregion

//#region data
const store = useStore();
const { t } = useI18n();

const focusInput = ref(false);
const readyFilter = ref(false);
//#endregion

//#region computed
const currencies = computed(() => store.getters['misc/currencies']);

const optionsFilterSelect = computed(() => [
  {
    label: t('affiliates.offersView.holdRanges.minutes'),
    value: 'minutes',
  },
  {
    label: t('affiliates.offersView.holdRanges.hours'),
    value: 'hours',
  },
  {
    label: t('affiliates.offersView.holdRanges.days'),
    value: 'days',
  },
  {
    label: t('affiliates.offersView.holdRanges.weeks'),
    value: 'weeks',
  },
]);

const isDaterangePicker = computed(() => {
  return props.columnFilters[props.column.property].op === '=';
});

const findFieldCol = computed(() => {
  return props.fields.find((el) => el.name === props.column.property);
});

const filterable = computed(() => {
  return findFieldCol.value !== undefined ? findFieldCol.value.filterable || false : false;
});

const allowedOperators = computed(() => {
  return findFieldCol.value !== undefined ? findFieldCol.value.allowedOperators || ['<', '=', '>'] : ['<', '=', '>'];
});

const filterInputType = computed(() => {
  return findFieldCol.value !== undefined ? findFieldCol.value.filterInputType || 'text' : 'text';
});

const openFilter = computed(() => {
  return (
    props.columnFilters[props.column.property]
    && (props.lastOpenFilter === props.column.property
      || ((props.columnFilters[props.column.property].value
          || props.columnFilters[props.column.property].dateRange !== null)
        && props.columnFilters[props.column.property].op))
  );
});

const filterInputHiddenClass = computed(() => {
  return props.customFilterColumn.indexOf(props.column.property) >= 0
  || props.customFilterColumnCreated.includes(props.column.property)
    ? 'filter-input__hide'
    : '';
});

const showTabs = computed(() => {
  return props.customFilterColumnCreated.includes(props.column.property)
    ? props.customFilterColumnCreated.includes(props.column.property)
    : props.customFilterColumn.indexOf(props.column.property) >= 0;
});
//#endregion

//#region methods
const getCurrencyLabel = (field) => {
  if (field?.currencyKey) {
    return '';
  }

  if (props.currency && [
    'currency',
    'formatMoney',
    '-formatMoney',
    'ncurrency',
    '-ncurrency',
  ].includes(field?.format)) {
    const currency = currencies.value.find(((el) => el.code === props.currency));

    return `, ${currency?.symbol || props.currency}`;
  }

  return '';
};

const changeExclude = (checked) => {
  emit('changeExclude', props.column.property, checked);
  checkReadyFilter();
};

const handlerRadioBtn = (value) => {
  emit('handlerRadioBtn', props.column.property, value);
};

const handlerFilter = () => {
  const columnFiltersClone = props.columnFilters;
  const set = !columnFiltersClone[props.column.property].visible;

  columnFiltersClone[props.column.property].visible = set;

  if (set) {
    if (props.lastOpenFilter) {
      columnFiltersClone[props.lastOpenFilter].visible = false;
    }
    emit('changeColumnFilters', columnFiltersClone);
    emit('lastOpenFilter', props.column.property);
  } else {
    emit('changeColumnFilters', columnFiltersClone);
    emit('lastOpenFilter', '');
  }
};

const toValueAndOp = (valueRange, isExclude) => {
  if (valueRange[0] !== '' && valueRange[1] !== '') {
    return {
      value: [valueRange[0], valueRange[1]],
      op: isExclude ? 'notBetween' : 'between',
    };
  }
  if (valueRange[0] !== '') {
    return {
      value: valueRange[0],
      op: isExclude ? '>' : '>=',
    };
  }
  return {
    value: valueRange[1],
    op: isExclude ? '<' : '<=',
  };
};

const handlerSuccessBtn = () => {
  const columnFiltersClone = props.columnFilters;
  columnFiltersClone[props.column.property].visible = false;

  if (filterInputHiddenClass.value !== 'filter-input__hide') {
    const { valueRange } = columnFiltersClone[props.column.property];
    const { isExclude } = columnFiltersClone[props.column.property];

    columnFiltersClone[props.column.property].op = toValueAndOp(valueRange, isExclude).op;
    columnFiltersClone[props.column.property].value = toValueAndOp(valueRange, isExclude).value;
  }

  emit('lastOpenFilter', '');
  emit('filter', columnFiltersClone);
  emit('setCheckApplyFilter', true);
  nextTick(() => {
    emit('setCheckApplyFilter', false);
  });
  readyFilter.value = false;
};

const handlerResetBtn = () => {
  const columnFiltersClone = cloneDeep(props.columnFilters);
  columnFiltersClone[props.column.property].op = '';
  columnFiltersClone[props.column.property].value = '';
  columnFiltersClone[props.column.property].valueRange = ['', ''];
  columnFiltersClone[props.column.property].isExclude = false;
  columnFiltersClone[props.column.property].valueSec = '';
  columnFiltersClone[props.column.property].dateFilter = '';
  columnFiltersClone[props.column.property].dateRange = null;
  columnFiltersClone[props.column.property].visible = false;
  emit('lastOpenFilter', '');
  emit('filter', columnFiltersClone, 'reset');
  emit('setCheckApplyFilter', true);
  nextTick(() => {
    emit('setCheckApplyFilter', false);
  });
};

const handlerDatepickerRange = (date, type) => {
  const formatDate = () => {
    if(date) {
      if (type === 'daterange') {
        return date.map((elem) => format(elem, 'yyyy-MM-dd'));
      }
      return format(date, 'yyyy-MM-dd');
    } else {
      return date;
    }
  };
  emit('handlerDatepickerRange', props.column.property, formatDate());
};

const handlerRangeValue = (val, type) => {
  emit('handlerRangeValue', type, props.column.property, val.replace());
  checkReadyFilter();
};

const checkReadyFilter = () => {
  readyFilter.value = !!(
    props.columnFilters[props.column.property].valueRange[0] || props.columnFilters[props.column.property].valueRange[1]
  );
};

const handlerColumnFilterInput = (val, type) => {
  emit('handlerColumnFilterInput', type, props.column.property, val);
};

const handlerCustomHeader = () => {
  if (filterable.value) {
    if (props.lastOpenFilter) {
      props.columnFilters[props.lastOpenFilter].visible = false;
      emit('lastOpenFilter', '');
    }
  }

  if (props.column.sortable || props.customSorting) {
    const paramSort = [
      {
        prop: props.column.property,
        order: props.sortOrder === 'asc' ? 'desc' : 'asc',
      },
      props.column.id,
    ];

    emit('handleSort', ...paramSort);
  }
};

const createValueSelect = () => {
  return (
    props.columnFilters[props.column.property].dateFilter
    || `${
      props.customFilterColumn.indexOf(props.column.property) >= 0
        ? (props.columnFilters[props.column.property].dateFilter = 'hours')
        : ''
    }`
  );
};
//#endregion
</script>

<template>
  <div
    class="custom-header"
    :class="[
      customSorting && column.property === sortProp ? sortOrder : '',
      column.sortable === false ? '' : 'custom-header_no-sort',
    ]"
    @click="handlerCustomHeader"
  >
    <div class="custom-header__title">
      {{ column.label }}{{ getCurrencyLabel(findFieldCol) }}
    </div>
    <span
      v-if="customSorting"
      class="custom-header__sorting"
    >
      <UiIcon
        name="caret-up"
        :size="14"
        color="inherit"
        class="custom-header__sorting-item custom-header__sorting-item_asc"
      />
      <UiIcon
        name="caret-down"
        :size="14"
        color="inherit"
        class="custom-header__sorting-item custom-header__sorting-item_desc"
      />
    </span>

    <span
      v-if="filterable && isFiltersInit"
      :class="[
        'custom-header__filters',
        { 'custom-header__filters_open': openFilter }
      ]"
    >
      <ElPopover
        trigger="click"
        width="auto"
        popper-class="custom-header__filters-popper"
        :visible="columnFilters[column.property].visible"
      >
        <div class="custom-header__filters-wrapper t:space-y-8">
          <UiTabs
            v-if="showTabs"
            v-model="columnFilters[column.property].op"
            :list="Array.isArray(allowedOperators) ? allowedOperators : []"
            @change="handlerRadioBtn"
          />
          <UiDatePicker
            v-if="customFilterColumnCreated.includes(column.property)"
            v-model="columnFilters[column.property].dateRange"
            class="custom-header__datepicker"
            :disabled="columnFilters[column.property].op === ''"
            :placeholder="isDaterangePicker ? $t('reports.other.start_end_date') : $t(`team_popup.see_stats_placeholder`)"
            :multi-calendars="isDaterangePicker"
            :range="isDaterangePicker"
            :always-clearable="false"
            :clearable="false"
            auto-apply
            :ui="{
              menu: 'custom-header__datepicker-menu'
            }"
            @update:model-value="isDaterangePicker ? handlerDatepickerRange($event, 'daterange') : handlerDatepickerRange($event, 'date')"
          />
          <div class="t:flex t:justify-between">
            <UiButton
              type="negative"
              icon="times"
              @click="handlerResetBtn"
            >
              {{ $t('ui.table.reset') }}
            </UiButton>
            <UiButton
              icon="check"
              :disabled="!readyFilter && !columnFilters[column.property].valueSec && !columnFilters[column.property].dateRange"
              @click="handlerSuccessBtn"
            >
              {{ $t('ui.table.apply') }}
            </UiButton>
          </div>
        </div>
        <template v-slot:reference>
          <div
            :class="[
              'custom-header__filters-trigger',
              { 'custom-header__filters-trigger_visible': openFilter }
            ]"
            @click.stop="handlerFilter"
          >
            <UiIcon
              substyle="fas"
              name="filter"
              :size="8"
              :color="openFilter ? 'var(--table-header)' : 'var(--header-text)'"
            />
          </div>
        </template>
      </ElPopover>
    </span>
  </div>
</template>

<style lang="scss">
.custom-header {
  height: 100%;
  line-height: 16px;
  display: flex;
  align-items: center;
  padding: 0;
  justify-content: space-between;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  &__title {
    padding: 0;
    max-width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    width: 100%;
    line-height: 16px;
  }
  &__sorting{
    visibility: hidden;
  }
  &__datepicker{
    width: 230px;
  }
  &__sorting-item {
    &_desc {
      margin-top: -9px;
    }
  }

  &__filters {
    display: flex;
    flex-direction: row;
    visibility: hidden;
    flex-grow: 10;
    justify-content: flex-end;
    margin-left: 4px;
    &_open {
      visibility: visible;
    }
  }
  &__filters-trigger {
    cursor: pointer;
    user-select: none;
    outline: none;
    border: none;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    line-height: 1;
    width: 15px;
    height: 15px;
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.3);
    &_visible{
      background-color: var(--table-header-text-active);
    }
  }
  &__filters-popper.el-popover {
    border-radius: 5px;
    background: #FFF;
    padding: 12px;
  }
  &__filters-wrapper {

  }
  &_no-sort {
    pointer-events: painted;
  }
}
</style>
